import {
  getDesktopChatAuthScope,
  subscribeDesktopChatAuthScope,
} from "@/desktop/desktop-chat-auth-scope";
import type { FrameApplyContext } from "@/lib/companion-frame-reducer";
import { withMutationEmitSuppressed } from "@/lib/mirrored-store";
import type { CliChoice } from "@/components/ui/cli-logo";
import { useWorkToastStore, type WorkToastItem } from "@/stores/work-toast-store";
import { createWorkToastItem, payloadString } from "./work-toast-item";

type Payload = Record<string, unknown>;
type Context = Pick<FrameApplyContext, "workToastStore">;

/** setTimeout's largest delay. */
const MAX_TIMER_MS = 2_147_483_647;
const PROVIDERS: readonly CliChoice[] = ["claude", "codex", "opencode"];

// An approval this window saw end never returns from a redelivered frame or
// from a summary fetched before it ended. Ids live for the signed-in webview.
const ended = new Set<string>();
// The session clear that follows an account change empties the cards without
// ending them, so only removals within one auth scope count as endings.
let itemsScope = getDesktopChatAuthScope();
let expiryTimer: ReturnType<typeof setTimeout> | null = null;
let expiryAt = Number.POSITIVE_INFINITY;

/** During the onboarding demo its own card answers the chosen task's approvals. */
function ownedByOnboarding(
  ctx: Pick<FrameApplyContext, "onboardingStore">,
  projectId: string,
  taskId: string,
): boolean {
  const {
    restarting,
    step,
    contextualStory: story,
  } = ctx.onboardingStore.getState();
  return (
    !restarting &&
    step === "desktop_usage_primer" &&
    story?.projectId === projectId &&
    (story.acceptedTaskIds?.includes(taskId) ?? false)
  );
}

function approvalDescription(
  taskTitle: string | null,
  title: string,
  summary: string | null,
): string {
  // A shell tool's summary is its command, which the title already says.
  const detail = summary && !title.endsWith(summary) ? summary : null;
  return [taskTitle && `Task: ${taskTitle}`, detail].filter(Boolean).join("\n");
}

/** Shows a pending coding-session approval as an approval card. The live
 *  `direct_session.approval_requested` frame and a restored summary row carry
 *  the same fields, so both are built here. */
export function applyToolApprovalRequested(
  data: Payload,
  ctx: FrameApplyContext,
  now: number,
): void {
  const approvalId = payloadString(data, "id");
  const projectId = payloadString(data, "project_id");
  const taskId = payloadString(data, "task_id");
  const title = payloadString(data, "title");
  if (!approvalId || !projectId || !taskId || !title) return;
  const key = `approval:${approvalId}`;
  const store = ctx.workToastStore.getState();
  const expires = Date.parse(payloadString(data, "expires_at") ?? "");
  const expiresAt = Number.isFinite(expires) ? expires : null;
  if (
    store.items[key] ||
    ended.has(approvalId) ||
    (expiresAt !== null && expiresAt <= now)
  )
    return;
  if (ownedByOnboarding(ctx, projectId, taskId)) return;
  store.upsertItem(
    createWorkToastItem(
      {
        key,
        source: "approval",
        tracked: false,
        state: "approval",
        agentName: payloadString(data, "agent_name") ?? "Agent",
        codingProvider:
          PROVIDERS.find((provider) => provider === data.provider) ?? null,
        projectId,
        taskId,
        detail: title,
        approval: {
          approvalId,
          title,
          expiresAt,
          description: approvalDescription(
            payloadString(data, "task_title"),
            title,
            payloadString(data, "summary"),
          ),
        },
      },
      now,
    ),
  );
}

/** The approval was answered, expired or its turn ended: the card leaves for
 *  good, in every window that sees its removal. */
export function endToolApproval(ctx: Context, approvalId: string): void {
  ctx.workToastStore.getState().removeItem(`approval:${approvalId}`);
}

/** `task.updated` is the only signal that a task's approvals ended. A row
 *  without `pending_approval_ids` (an older server) keeps a waiting session's cards. */
export function applyToolApprovalTaskUpdate(task: Payload, ctx: Context): void {
  const taskId = payloadString(task, "id");
  if (!taskId) return;
  const session = task.direct_session as
    | { status?: unknown; pending_approval_ids?: unknown }
    | null
    | undefined;
  const ids = session?.pending_approval_ids;
  const pendingIds: unknown[] | null = Array.isArray(ids) ? ids : null;
  for (const item of Object.values(ctx.workToastStore.getState().items)) {
    const approval = item.approval;
    if (!approval || item.taskId !== taskId) continue;
    const waiting =
      session?.status === "waiting_approval" &&
      (!pendingIds || pendingIds.includes(approval.approvalId));
    if (!waiting) endToolApproval(ctx, approval.approvalId);
  }
}

// Every window holds its own copy of the cards, however they arrived, so each
// expires its own without crossing windows.
function expireToolApprovals(): void {
  expiryTimer = null;
  expiryAt = Number.POSITIVE_INFINITY;
  const now = Date.now();
  withMutationEmitSuppressed(() => {
    for (const item of Object.values(useWorkToastStore.getState().items)) {
      const approval = item.approval;
      if (approval?.expiresAt != null && approval.expiresAt <= now) {
        endToolApproval(
          { workToastStore: useWorkToastStore },
          approval.approvalId,
        );
      }
    }
  });
  scheduleToolApprovalExpiry(useWorkToastStore.getState().items);
}

/** One timer, a second after the earliest card expires. */
function scheduleToolApprovalExpiry(
  items: Record<string, WorkToastItem>,
): void {
  const next = Math.min(
    ...Object.values(items).map(
      (item) => item.approval?.expiresAt ?? Number.POSITIVE_INFINITY,
    ),
  );
  if (next === expiryAt) return;
  if (expiryTimer) clearTimeout(expiryTimer);
  expiryTimer = null;
  expiryAt = next;
  if (!Number.isFinite(next)) return;
  expiryTimer = setTimeout(
    expireToolApprovals,
    Math.min(Math.max(next - Date.now(), 0) + 1_000, MAX_TIMER_MS),
  );
}

// However a card leaves, here or mirrored from another window, it ended.
useWorkToastStore.subscribe((state, previous) => {
  if (state.items === previous.items) return;
  const scope = getDesktopChatAuthScope();
  if (scope === itemsScope) {
    for (const [key, item] of Object.entries(previous.items)) {
      const approval = item.approval;
      if (approval && !state.items[key]) ended.add(approval.approvalId);
    }
  }
  itemsScope = scope;
  scheduleToolApprovalExpiry(state.items);
});

subscribeDesktopChatAuthScope(() => {
  ended.clear();
  if (expiryTimer) clearTimeout(expiryTimer);
  expiryTimer = null;
  expiryAt = Number.POSITIVE_INFINITY;
});
